/**
 * The Reconciliation tab: the new Statements view (#441) and, for one more
 * release, the flat "Legacy lines" view that also holds the upload form.
 *
 * Broker admin and super admin see both; everyone else gets the tab exactly as
 * it was (the verification API refuses them anyway).
 */

import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { CommissionComparisonTab } from './CommissionComparisonTab';
import { StatementsView } from './StatementsView';

export function CommissionReconciliationTab() {
  const user = useAuthStore((s) => s.user);
  const [params, setParams] = useSearchParams();
  const canView = user?.role === 'broker_admin' || user?.role === 'super_admin';
  const canAct = user?.role === 'broker_admin';
  if (!canView) return <CommissionComparisonTab />;

  const view = params.get('view') === 'legacy' ? 'legacy' : 'statements';
  const go = (v: 'statements' | 'legacy') => {
    const next = new URLSearchParams(params);
    if (v === 'legacy') next.set('view', 'legacy');
    else next.delete('view');
    setParams(next);
  };

  const tab = (id: 'statements' | 'legacy', label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={view === id}
      onClick={() => go(id)}
      className={`border-b-2 px-3 py-2 text-sm font-medium ${
        view === id
          ? 'border-primary-600 text-primary-700 dark:text-primary-400'
          : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div>
      <div role="tablist" aria-label="Reconciliation views" className="mb-4 flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {tab('statements', 'Statements')}
        {tab('legacy', 'Legacy lines and upload')}
      </div>
      {view === 'statements' ? <StatementsView canAct={canAct} onUpload={() => go('legacy')} /> : <CommissionComparisonTab />}
    </div>
  );
}
